import { useEffect } from "react";
import { isEqual, isString, isObject, isArray, isNumber, get } from "lodash-es";
import { Signal, signal, useSignalEffect, batch, useSignal } from "@preact/signals-react";

import { makeSlug, generateUID } from "./utils";
import { meta, getState, getDefaultContext } from "./dataService";

import { client } from "./nats-client";
import { users, getTimeDifference } from "./users";
import { syncOverlay } from "./syncOverlay";

import { log } from "../library/logger.js";

const tag = "NGSync";

const types = {
  STRING: "String",
  OBJECT: "Object",
  ARRAY: "Array",
  NUMBER: "Number",
};

function getPageId() {
  let pageId = window.location.pathname;
  if (pageId.startsWith("/local")) pageId = pageId.replace("/", "");
  pageId = makeSlug(pageId);
  return pageId;
}

function getSessionId(pageId) {
  return sessionStorage.getItem(`${pageId}-sessionId`) || "";
}

function setSessionId(pageId) {
  let sessionId = getSessionId(pageId);
  if (!sessionId) {
    sessionId = generateUID();
    sessionStorage.setItem(`${pageId}-sessionId`, sessionId);
  }
  return sessionId;
}

const ignoreState = [
  "SelectedId",
  "SyncId",
  "IgnoreState",
  "SyncInitialized",
  "Users",
  "UserColors",
  "SyncSubscribed",
  "ComponentSyncForm",
];

type SyncState = {
  ClientId: Signal<string | number>;
  SyncId: Signal<string | null>;
  SyncData: Signal<object>;
  SyncInitialized: Signal<boolean>;
  SyncSubscribed: Signal<boolean>;
  Doc: Signal<{ [key: string]: number } | null>;
  Types: Signal<object>;
  UpdateInProgress: Signal<boolean>;
  ContainerId: Signal<string>;
};

type User = {
  Color: string;
  Email: string;
  Id: string;
  IsActive: boolean;
  Name: string;
  Time: string;
};

type UserState = {
  UserColors: Signal<string[] | null>;
  Users: Signal<User[]>;
  UserCreated: Signal<boolean>;
  AvatarColor: Signal<string | null>;
  ColorInitialized: Signal<boolean>;
  PreviouslyPublishedUsers: Signal<User[]>;
  LastPublishedTime: Signal<Date | null>;
};

type PageState = {
  Initialized: Signal<boolean>;
  SyncDataOrigin: Signal<string>;
  StateUpdate: Signal<string[]>;
  Stream: Signal<string>;
  SessionId: Signal<string>;
  ClientId: Signal<string | number>;
  PageId: Signal<string>;
  PageSyncData: Signal<object>;
  TargetId: Signal<string>;
  DisableFunctionality: Signal<boolean>;
  ListenersInitialized: Signal<boolean>;
  SaveReceived: Signal<boolean>;
  PageVersion: Signal<number>;
  NewDataIdOrigin: Signal<string>;
};

type OverlayState = {
  OverlayCollection: Signal<object>;
  SyncId: Signal<string | null>;
  OverlayInitialized: Signal<boolean>;
  PreviouslyPublishedCollection: Signal<object>;
};

type entityData = {
  clientId: number;
  differences: [number, string][];
  doc: { [key: string]: number };
  id: string;
  key: string;
  sessionId: string;
  source: string;
  syncData: string;
  syncId: string;
  time: string;
  value: string;
  version: number;
};

type stateState = {
  StateUpdateCollection: Signal<object>;
};

const userState: UserState = {
  Users: signal([]),
  UserColors: signal(null),
  UserCreated: signal(false),
  AvatarColor: signal(null),
  ColorInitialized: signal(false),
  PreviouslyPublishedUsers: signal([]),
  LastPublishedTime: signal(null),
};

const pageState: PageState = {
  Initialized: signal(false),
  SyncDataOrigin: signal(""),
  StateUpdate: signal([]),
  Stream: signal("synchronization"),
  SessionId: signal(""),
  ClientId: signal(""),
  PageId: signal(""),
  PageSyncData: signal({}),
  TargetId: signal(""),
  DisableFunctionality: signal(false),
  ListenersInitialized: signal(false),
  SaveReceived: signal(false),
  PageVersion: signal(0),
  NewDataIdOrigin: signal(""),
};

const overlayState: OverlayState = {
  OverlayCollection: signal({}),
  PreviouslyPublishedCollection: signal({}),
  SyncId: signal(null),
  OverlayInitialized: signal(false),
};

const stateState = {
  StateUpdateCollection: signal({}),
};

const containerState: Record<string, SyncState> = {};

export function useSync(config, context) {
  const { state } = getState(context);

  const UNRESPONSIVE_LIMIT = 4000;
  const INACTIVE_LIMIT = 500;
  const ACTIVE_STATUS_OUTPUT_FREQUENCY = 2000;
  const ACTIVE_STATUS_CHECK_FREQUENCY = 2000;
  const PAGE_TO_UPDATE_WAIT_TIME = 10;

  const automergeImport: any = useSignal(null);
  const fastDiffImport: any = useSignal(null);
  useEffect(() => {
    const loadModule = async () => {
      const automerge = await import("@automerge/automerge");
      const { default: diff } = await import("@/lib/fastdiff/fast-diff.es.js");
      automergeImport.value = automerge;
      fastDiffImport.value = diff;
    };
    loadModule();
  }, []);

  const Automerge = automergeImport.value;
  const diff = fastDiffImport.value;

  function initData(data) {
    let Doc = Automerge.init();
    const Types = [];
    for (const [key, value] of Object.entries(data)) {
      if (isString(value)) {
        Doc = Automerge.change(Doc, (newDoc: any) => {
          newDoc[key] = new Automerge.Text(value);
        });
        Types[key] = types.STRING;
      } else if (isNumber(value)) {
        const v = value.toString();
        Doc = Automerge.change(Doc, (newDoc: any) => {
          newDoc[key] = new Automerge.Text(v);
        });
        Types[key] = types.NUMBER;
      } else if (isArray(value)) {
        Doc = Automerge.change(Doc, (newDoc: any) => {
          const stringified = JSON.stringify(value);
          newDoc[key] = new Automerge.Text(stringified);
        });
        Types[key] = types.ARRAY;
      } else if (isObject(value)) {
        Doc = Automerge.change(Doc, (newDoc: any) => {
          const stringified = JSON.stringify(value);
          newDoc[key] = new Automerge.Text(stringified);
        });
        Types[key] = types.OBJECT;
      }
    }
    return { Doc, Types };
  }
  function parsePath(path) {
    const keys: string[] = [];
    const regex = /([^[\].]+)/g;
    let match;

    // Get all parts of the path
    while ((match = regex.exec(path))) {
      keys.push(match[0]);
    }

    return keys;
  }

  function setValue(Doc, key, value) {
    const originalDoc = Doc.value;
    Doc.value = Automerge.change(originalDoc, (doc: any) => {
      const path = parsePath(key);
      const k = path[0];
      doc[k] = new Automerge.Text(value);
    });
  }

  async function getValue(doc, types, key) {
    const json = await toJSON(doc, types);
    return get(json, key);
  }

  function toJSON(Doc, Types) {
    const json = {};

    function convertStringToNumber(str) {
      if (str === "") {
        return null;
      }
      const num = Number(str);

      if (isNaN(num)) {
        return null;
      }

      return num;
    }

    for (const [key, value] of Object.entries(Doc)) {
      if (Types[key] === types.STRING) {
        json[key] = (value as any).toString();
      } else if (Types[key] === types.NUMBER) {
        json[key] = convertStringToNumber((value as any).toString());
      } else if (Types[key] === types.OBJECT) {
        json[key] = JSON.parse(value as string);
      } else if (Types[key] === types.ARRAY) {
        json[key] = JSON.parse(value as string);
      }
    }
    return json;
  }
  function getDifferences(Doc, Types, key, oldValue, newValue) {
    const path = parsePath(key);
    const rootPath = path[0];
    let o = oldValue;
    let n = newValue;

    function applyValueFromPath(obj, keys, value) {
      const k = JSON.parse(JSON.stringify(keys));
      let last = k.pop();
      let current = obj;
      for (let key of k) {
        if (Array.isArray(current)) {
          const index = isNaN(key) ? current.findIndex((el) => el[key] !== undefined) : parseInt(key, 10);
          current = current[index];
        } else if (typeof current === "object" && current !== null) {
          current = current[key];
        }
      }
      current[last] = value;
      return obj;
    }

    if (!isString(o)) {
      o = o === null ? "" : JSON.stringify(o);
    }
    if (!isString(n)) {
      n = n === null ? "" : JSON.stringify(n);
    }
    if (path.length > 1 && (Types[rootPath] === types.OBJECT || Types[rootPath] === types.ARRAY)) {
      const updated = applyValueFromPath(toJSON(Doc, Types), path, newValue);
      return diff(Doc[rootPath].toString(), JSON.stringify(updated[rootPath]));
    }
    return diff(o, n);
  }

  function updateDoc(Doc, types, key, differences) {
    const originalDoc = Doc.value;

    Doc.value = Automerge.change(originalDoc, (doc: any) => {
      const path = parsePath(key);
      const k = path[0];
      let str = doc[k];
      if (!str) {
        doc[k] = new Automerge.Text("");
        str = doc[k];
        types[k] = types.STRING;
      }

      let index = 0;

      differences.forEach(([op, v]) => {
        if (op === diff.EQUAL) {
          index += v.length;
        } else if (op === diff.INSERT) {
          str.insertAt(index, v);
        } else if (op === diff.DELETE) {
          str.deleteAt(index, v.length);
        }
      });
    });
  }
  function isDifferent(differences) {
    if (differences.length === 0) {
      return false;
    } else if (differences.length == 1) {
      return differences[0][0] !== diff.EQUAL;
    }
    return true;
  }

  function encodeDoc(Doc) {
    return Automerge.save(Doc);
  }

  function decodeDoc(doc) {
    const array: number[] = Object.values(doc);
    const docArray = new Uint8Array(array);
    return Automerge.load(docArray);
  }

  useSignalEffect(() => {
    if (!pageState.Initialized.peek()) {
      const pageId = getPageId();
      batch(() => {
        pageState.SyncDataOrigin.value = config.SyncDataOrigin;
        pageState.StateUpdate.value = config.StateUpdate;
        pageState.NewDataIdOrigin.value = config.NewDataIdOrigin;
        pageState.PageId.value = pageId;
        pageState.SessionId.value = setSessionId(pageId);
        pageState.Initialized.value = true;
        pageState.ClientId.value = client.getId();
      });

      state.SyncDeletedId = signal("");
      state.SyncIsSaved = signal(false);
      state.SyncUpdate = signal({});
      state.SyncStateUpdate = signal({});
      state.SelectedId = signal(null);
      state.CodeIsValid = signal(true);
    }

    const containerId = config.ContainerId;
    if (!containerState[containerId]) {
      containerState[containerId] = {
        ClientId: signal(client.getId()),
        ContainerId: signal(containerId),
        SyncId: signal(null),
        SyncData: signal({}),
        SyncInitialized: signal(false),
        SyncSubscribed: signal(false),
        Doc: signal(null),
        Types: signal({}),
        UpdateInProgress: signal(false),
      };
    }

    const stream = pageState.Stream.peek();
    const pageId = pageState.PageId.peek();
    client.checkKeyExist(stream, pageId, "user", (keyExist) => {
      if (!keyExist) {
        userState.ColorInitialized.value = true;
      }
    });

    return () => {
      pageState.Initialized.value = false;
    };
  });

  useSignalEffect(() => {
    const containerId = config.ContainerId;

    const syncState = containerState[containerId];
    const key = pageState.SyncDataOrigin.peek();
    const users = userState.Users.peek();
    let data = state?.[key]?.value;
    if (data && Object.keys(data).length > 0) {
      if (syncState.Doc.peek()) {
        const docData = toJSON(syncState.Doc.peek(), syncState.Types.peek());
        if (isEqual(docData, data)) return;
      }

      const syncId = syncState.SyncId.peek();
      if (syncId) {
        const syncData = syncState.SyncData.peek()?.[syncId];
        if (syncData) {
          data = syncData;
          state[key].value = syncData;
        }
      }

      const { Doc, Types } = initData(data);
      batch(() => {
        syncState.Doc.value = Doc;
        syncState.Types.value = Types;
      });
    }
    if (users.length === 0) {
      userState.UserColors.value = [];
    }
  });

  useSignalEffect(() => {
    const colorsInitialized = userState.ColorInitialized.value;

    const userCreated = userState.UserCreated.peek();

    const User = state?.User.peek();

    if (!userCreated && colorsInitialized && User && User.Name) {
      const sessionId = setSessionId(pageState.PageId.peek());
      const newUser = users.createNewUser(User, userState.AvatarColor, userState.UserColors, sessionId);
      log.info(tag, "Creating new user", newUser);

      users.addToUsers(userState.Users, [newUser], INACTIVE_LIMIT, UNRESPONSIVE_LIMIT);

      userState.UserCreated.value = true;
    }
  });

  useSignalEffect(() => {
    const sessionId = pageState.SessionId.value;

    const inactiveInterval = setInterval(() => {
      users.deleteInactiveUsers(
        userState.Users,
        userState.UserColors,
        overlayState.OverlayCollection,
        sessionId,
        INACTIVE_LIMIT,
        UNRESPONSIVE_LIMIT
      );
    }, ACTIVE_STATUS_CHECK_FREQUENCY);

    const publishInterval = setInterval(() => {
      const u = userState.Users.peek();
      const time = new Date();
      const lastPublishedTime = userState.LastPublishedTime.peek();

      const userExist = u[0] && u[0]?.Id === pageState.SessionId.peek();
      const shouldUpdate =
        userExist && (!lastPublishedTime || getTimeDifference(time, lastPublishedTime) < UNRESPONSIVE_LIMIT);
      const updatedUser = { ...u[0], Time: time.toISOString() };

      if (shouldUpdate) {
        userState.Users.value = [updatedUser, ...u.slice(1)];
      }

      userState.LastPublishedTime.value = time;
    }, ACTIVE_STATUS_OUTPUT_FREQUENCY);

    return () => {
      clearInterval(inactiveInterval);
      clearInterval(publishInterval);
    };
  });

  useSignalEffect(() => {
    const stream = pageState.Stream.value;
    const pageId = pageState.PageId.value;
    let stopEntity: (() => void) | null | undefined = null;
    let stopUser: (() => void) | null | undefined = null;
    let stopOverlay: (() => void) | null | undefined = null;
    let stopSaved: (() => void) | null | undefined = null;
    let stopStateUpdate: (() => void) | null | undefined = null;
    if (!pageState.ListenersInitialized.value && stream && pageId) {
      const setupListeners = async () => {
        stopEntity = await client.syncKvJetstream(stream, pageId, "entity", (incoming: any) => {
          pageState.PageSyncData.value = incoming;

          Object.keys(incoming).forEach((containerId) => {
            const msg: entityData = incoming[containerId];
            const node = meta.getNode(msg.id);
            const context = node?.context || getDefaultContext();
            const { state, form } = getState(context);
            console.log("syncIncomingChanges", pageId, msg);

            const syncState = containerState[containerId];
            let isSyncInitialized = syncState.SyncInitialized.peek();
            let versionNotInSync = false;

            if (syncState.Doc.i > 1 && !isSyncInitialized) {
              syncState.SyncInitialized.value = isSyncInitialized = true;
            }

            if (msg.syncId) {
              syncState.SyncData.value = JSON.parse(msg.syncData);
            }

            if (!isSyncInitialized && msg.doc) {
              if (!msg.syncId) {
                const decodedDoc = decodeDoc(msg.doc);
                syncState.Doc.value = decodedDoc;
                state[pageState.SyncDataOrigin.value].value = toJSON(syncState.Doc.peek(), syncState.Types.peek());
              }
              pageState.PageVersion.value = msg.version;
              syncState.SyncInitialized.value = true;
              return;
            } else {
              if (msg.sessionId !== pageState.SessionId.peek()) {
                // tobeVersionNum need to be equal for regular update
                const tobeVersionNum = pageState.PageVersion.peek() + 1;
                if (msg.version) {
                  pageState.PageVersion.value = msg.version;
                }
                if (tobeVersionNum < msg.version) {
                  versionNotInSync = true;
                } else if (tobeVersionNum > msg.version) {
                  return;
                }
              }
            }

            if (msg.sessionId) {
              users.setActive(userState.Users, msg.sessionId, msg.time, INACTIVE_LIMIT);
            }

            const Doc = syncState.Doc.peek();
            const Types = syncState.Types.peek();
            if (!Doc || !Types) {
              return;
            }
            syncState.UpdateInProgress.value = true;

            const syncIdMismatch = syncState.SyncId.peek() !== msg.syncId;
            const key = msg.key;
            const val = getValue(Doc, Types, key);
            const originalValue = isString(val) ? val : JSON.stringify(val);
            const valueUnchanged = isEqual(originalValue, msg.value);

            let valueOrigin = form?.[key];

            updateIncomingChange(msg, containerId, syncIdMismatch, valueUnchanged, valueOrigin, versionNotInSync);

            syncState.UpdateInProgress.value = false;
          });
        });

        stopUser = await client.syncKvJetstream(stream, pageId, "user", (msg: any) => {
          if (!userState.ColorInitialized.peek()) {
            batch(() => {
              userState.UserColors.value = msg.colors;
              userState.ColorInitialized.value = true;
            });
          }

          if (msg?.sessionId === pageState.SessionId.peek()) return;
          if (msg.clientId == pageState.ClientId.peek()) return;

          const incomingUsers: any = JSON.parse(msg.users);
          userState.PreviouslyPublishedUsers.value = incomingUsers;
          const lastPublishedTime = userState.LastPublishedTime.peek();
          const time = new Date();
          const shouldUpdate = !lastPublishedTime || getTimeDifference(time, lastPublishedTime) < UNRESPONSIVE_LIMIT;

          if (shouldUpdate) {
            users.addToUsers(userState.Users, incomingUsers, INACTIVE_LIMIT, UNRESPONSIVE_LIMIT);
            overlayState.OverlayCollection.value = { ...overlayState.OverlayCollection.peek() };
          }
        });

        stopOverlay = await client.syncKvJetstream(stream, pageId, "overlay", (msg: any) => {
          const sessionId = pageState.SessionId.peek();
          const c = JSON.parse(msg.collection);
          const collection = overlayState.OverlayCollection.peek();

          if (overlayState.OverlayInitialized.peek() && msg.sessionId === sessionId) return;

          overlayState.PreviouslyPublishedCollection.value = JSON.parse(JSON.stringify(c));

          if (collection[sessionId]) {
            c[sessionId] = collection[sessionId];
          }

          overlayState.OverlayCollection.value = c;
        });

        stopSaved = await client.syncKvJetstream(stream, pageId, "savedState", (msg: any) => {
          if (msg.sessionId == pageState.SessionId.peek()) return;
          if (msg.syncIsSaved) {
            batch(() => {
              state.SyncIsSaved.value = true;
              pageState.SaveReceived.value = true;
            });

            if (msg.savedSyncId === overlayState.SyncId.peek()) {
              state[pageState.SyncDataOrigin.value].value = null;
            }
          }
        });

        stopStateUpdate = await client.syncKvJetstream(stream, pageId, "stateUpdate", (msg: any) => {
          stateState.StateUpdateCollection.value = msg.stateUpdateCollection;
          if (msg.sessionId == pageState.SessionId.peek()) return;
          for (const [key, value] of Object.entries(msg.stateUpdateCollection)) {
            if (!isEqual(state[key].peek(), value)) {
              console.log(`Sync state update: updating state ${key} with`, value);
              state[key].value = value;
            }
          }
        });
      };

      setupListeners();
      pageState.ListenersInitialized.value = true;

      return () => {
        if (stopEntity) {
          stopEntity();
        }
        if (stopUser) {
          stopUser();
        }
        if (stopOverlay) {
          stopOverlay();
        }
        if (stopSaved) {
          stopSaved();
        }
        if (stopStateUpdate) {
          stopStateUpdate();
        }
      };
    }
  });

  useSignalEffect(() => {
    if (!state?.SyncUpdate) return;

    const { id, key, value } = state.SyncUpdate.value;
    const containerId = config.ContainerId;
    const syncState = containerState[containerId];

    if (syncState.UpdateInProgress.peek() || !key) return;
    if (pageState.DisableFunctionality.peek()) return;

    const targetId = pageState.TargetId.peek();
    const parentIds = getParentIds(targetId);

    if (!parentIds?.includes(containerId)) return;
    if (value == null) return;
    syncFormChange(id, key, value, containerId);
  });

  useSignalEffect(() => {
    if (!state?.SyncStateUpdate) return;
    const update = state.SyncStateUpdate.value;
    if (Object.keys(update).length === 0) return;

    const { key, value } = update;
    if (pageState.StateUpdate.peek().includes(key)) {
      publishStateChange({
        sessionId: pageState.SessionId.peek(),
        stateUpdateCollection: {
          ...stateState.StateUpdateCollection.peek(),
          [key]: value,
        },
      });
    }
  });

  useSignalEffect(() => {
    document.addEventListener("mouseup", handleSelection);
    document.addEventListener("keyup", handleSelection);
    return () => {
      document.removeEventListener("mouseup", handleSelection);
      document.removeEventListener("keyup", handleSelection);
    };
  });

  useSignalEffect(() => {
    const selectedId = state?.SelectedId?.value;
    const containerId = config.ContainerId;
    const syncState = containerState[containerId];
    if (selectedId && selectedId !== syncState.SyncId.value) {
      const sessionId = pageState.SessionId.peek();
      const overlayCollection = overlayState.OverlayCollection.peek();
      const selectedData = syncState.SyncData.peek()?.[selectedId];
      if (selectedData) {
        const { Doc, Types } = initData(selectedData);

        batch(() => {
          syncState.Doc.value = Doc;
          syncState.Types.value = Types;
        });
        state[pageState.SyncDataOrigin.value].value = toJSON(Doc, Types);
      }
      const updatedCollection = {
        ...overlayCollection,
        [sessionId]: { ...overlayCollection[sessionId], syncId: selectedId },
      };

      if (!overlayState.OverlayInitialized.peek()) {
        overlayState.OverlayInitialized.value = true;
      }

      // To wait for elements like themes form to load
      setTimeout(() => {
        batch(() => {
          overlayState.SyncId.value = selectedId;
          containerState[containerId].SyncId.value = selectedId;
          overlayState.OverlayCollection.value = updatedCollection;
        });
      }, PAGE_TO_UPDATE_WAIT_TIME);
    }
  });

  useSignalEffect(() => {
    const overlayCollection = overlayState.OverlayCollection.value;
    const overlayInitialized = overlayState.OverlayInitialized.peek();
    const PreviouslyPublishedCollection = overlayState.PreviouslyPublishedCollection.peek();

    if (overlayInitialized && !isEqual(PreviouslyPublishedCollection, overlayCollection)) {
      const userIds = userState.Users.peek().map((user) => user.Id);
      const collection = JSON.parse(JSON.stringify(overlayCollection));
      Object.keys(collection).forEach((id) => {
        if (!userIds.includes(id)) {
          delete collection[id];
        }
      });

      syncOverlay.publishOverlay(
        pageState.Stream.peek(),
        collection,
        pageState.PageId.peek(),
        pageState.SessionId.peek()
      );

      overlayState.PreviouslyPublishedCollection.value = collection;
    }
  });

  useSignalEffect(() => {
    const usersCollection = userState.Users.value;
    const PreviouslyPublishedUsers = userState.PreviouslyPublishedUsers.peek();

    if (userState.UserCreated && !isEqual(usersCollection, PreviouslyPublishedUsers)) {
      const activelyUsedUserColors = userState.Users.peek().map((user) => user.Color);
      users.publishUser(
        pageState.Stream.peek(),
        pageState.PageId.peek(),
        usersCollection,
        activelyUsedUserColors,
        pageState.SessionId.peek()
      );

      userState.UserColors.value = activelyUsedUserColors;
      userState.PreviouslyPublishedUsers.value = usersCollection;
    }
  });
  useSignalEffect(() => {
    if (!state?.SyncDeletedId) return;
    const syncDeletedId = state.SyncDeletedId.value;
    if (!syncDeletedId) return;

    const containerId = config.ContainerId;
    const syncState = containerState[containerId];
    const syncId = syncState.SyncId.peek();

    if (syncId) {
      const newSyncData = syncState.SyncData.peek();
      if (newSyncData[syncDeletedId]) {
        delete newSyncData[syncDeletedId];

        syncState.SyncData.value = newSyncData;

        const pageSyncData = pageState.PageSyncData.peek();
        pageState.PageSyncData.value = {
          ...pageSyncData,
          [containerId]: {
            ...pageSyncData[containerId],
            syncData: JSON.stringify(newSyncData),
          },
        };

        client.putKvJetstream(
          pageState.Stream.peek(),
          pageState.PageId.peek(),
          "entity",
          pageState.PageSyncData.peek()
        );
      }
    }
    state.SyncDeletedId.value = "";
  });

  useSignalEffect(() => {
    if (!state?.SyncIsSaved) return;

    const syncIsSaved = state.SyncIsSaved.value;
    if (!syncIsSaved) return;

    batch(() => {
      state.Status.value = "Saved changes";
      state.ShowStatus.value = true;
    });

    setTimeout(() => {
      state.SyncIsSaved.value = false;
    }, PAGE_TO_UPDATE_WAIT_TIME);

    let savedSyncId = null;
    if (pageState.SaveReceived.peek()) {
      pageState.SaveReceived.value = false;
    } else {
      const containerId = config.ContainerId;
      const syncState = containerState[containerId];
      const syncId = syncState.SyncId.peek();
      const key = pageState.SyncDataOrigin.peek();
      let data = state?.[key]?.peek();
      const deletedId = state?.SyncDeletedId.peek();
      if (deletedId) {
        savedSyncId = deletedId;
      } else if (syncId || Object.keys(data).length === 0) {
        const savedData = state.SyncSavedData.peek();
        const Id = savedData[pageState.NewDataIdOrigin.peek()] || generateUID();
        savedSyncId = Id;
        const savedInput = { ...savedData, Id };
        const newSyncData = { ...syncState.SyncData.peek(), [Id]: savedInput };

        syncState.SyncData.value = newSyncData;
        const pageSyncData = pageState.PageSyncData.peek();
        pageState.PageSyncData.value = {
          ...pageSyncData,
          [containerId]: {
            ...pageSyncData[containerId],
            syncData: JSON.stringify(newSyncData),
          },
        };
        client.putKvJetstream(
          pageState.Stream.peek(),
          pageState.PageId.peek(),
          "entity",
          pageState.PageSyncData.peek()
        );
      }
      publishSavedState({ sessionId: pageState.SessionId.peek(), syncIsSaved: true, savedSyncId });
    }
  });

  useSignalEffect(() => {
    if (!state?.SyncIsSaved) return;

    const syncIsSaved = state.SyncIsSaved.value;
    if (!syncIsSaved && state.SyncIsSaved.i > 0) {
      batch(() => {
        state.Status.value = "";
        state.ShowStatus.value = false;
      });
      publishSavedState({ sessionId: pageState.SessionId.peek(), syncIsSaved: false });
    }
  });

  function publishSavedState(saveData) {
    client.putKvJetstream(pageState.Stream.peek(), pageState.PageId.peek(), "savedState", saveData);
  }

  function publishStateChange(stateUpdate) {
    client.putKvJetstream(pageState.Stream.peek(), pageState.PageId.peek(), "stateUpdate", stateUpdate);
  }

  function getParentIds(targetId) {
    const formId = context.Form?.Config.Id || state.SyncStateUpdate.peek().id;
    const elementId = targetId ? targetId.replace(`${formId}-`, "") : "";

    return meta.getNode(elementId)?.pathIds;
  }

  function handleSelection(e) {
    const targetId = e.target?.closest("[data-testid]")?.getAttribute("data-testid");
    if (!targetId || targetId === pageState.TargetId.peek()) return;
    pageState.TargetId.value = targetId;

    const parentIds = getParentIds(targetId);
    let disable = true;
    if (parentIds) {
      for (const containerId of Object.keys(containerState)) {
        if (parentIds.includes(containerId)) disable = false;
      }
    }

    pageState.DisableFunctionality.value = disable;

    if (disable) return;

    const sessionId = pageState.SessionId.peek();
    const syncId = overlayState.SyncId.peek();
    const updatedCollection = {
      ...overlayState.OverlayCollection.peek(),
      [sessionId]: { id: targetId, ...(syncId && { syncId }) },
    };

    if (!overlayState.OverlayInitialized.peek()) {
      overlayState.OverlayInitialized.value = true;
    }
    overlayState.OverlayCollection.value = updatedCollection;
  }

  function updateIncomingChange(msg, containerId, syncIdMismatch, valueUnchanged, valueOrigin, versionNotInSync) {
    const { key, value, differences } = msg;

    const syncState = containerState[containerId];

    if (msg.clientId == syncState.ClientId.peek()) return;

    if (syncIdMismatch || valueOrigin === undefined) {
      return;
    }

    if (valueUnchanged) return;
    if (versionNotInSync) {
      setValue(syncState.Doc, key, value);
    } else {
      updateDoc(syncState.Doc, syncState.Types.peek(), key, differences);
    }
    valueOrigin.value = getValue(syncState.Doc.peek(), syncState.Types.peek(), key) || "";
  }

  function syncFormChange(id, key, value, containerId) {
    const syncState = containerState[containerId];
    const time = new Date().toISOString();
    const Doc = syncState.Doc.peek();
    const Types = syncState.Types.peek();
    const oldValue = getValue(Doc, Types, key) || "";
    const differences = getDifferences(Doc, Types, key, oldValue, value);
    const different = isDifferent(differences);

    if (different) {
      let sessionId = "";
      let data = "";

      updateDoc(syncState.Doc, Types, key, differences);
      const updatedDoc = syncState.Doc.peek();
      const encodedDoc = encodeDoc(updatedDoc);
      const syncId = syncState.SyncId.peek();
      const clientId = syncState.ClientId.peek();

      pageState.PageVersion.value += 1;

      if (syncId) {
        const json = toJSON(updatedDoc, Types);
        const newSyncData = { ...syncState.SyncData.peek(), [syncId]: json };

        data = JSON.stringify(newSyncData);
        syncState.SyncData.value = newSyncData;
      }

      if (userState.Users.peek().length > 0) {
        sessionId = pageState.SessionId.peek();
      }

      pageState.PageSyncData.value = {
        ...pageState.PageSyncData.peek(),
        [containerId]: {
          id,
          key,
          doc: encodedDoc,
          value,
          syncData: data,
          clientId: clientId,
          source: "Form",
          syncId,
          differences,
          time,
          sessionId,
          version: pageState.PageVersion.peek(),
        },
      };
      client.putKvJetstream(pageState.Stream.peek(), pageState.PageId.peek(), "entity", pageState.PageSyncData.peek());
    }
  }

  return {
    Users: userState.Users,
    SyncId: overlayState.SyncId,
    OverlayCollection: overlayState.OverlayCollection,
  };
}
